import { queryOptions } from '@tanstack/react-query';

import { acceptCsrfToken, api, ensureCsrf } from '../client';
import { csrfStore } from '../csrf';
import { endpoints } from '../endpoints';
import { ApiError, type AuthSession, type DeviceBinding, type GameToken, type Viewer } from '../types';
import {
  authSession,
  deviceBinding,
  gameToken,
  viewer as normalizeViewer,
  type WireDevice,
  type WireGameToken,
  type WirePlayerSelf,
  type WireSession,
} from '../wire';

export interface LoginInput {
  identifier: string;
  password: string;
  remember?: boolean;
}

export interface RegisterInput {
  username: string;
  email: string;
  password: string;
  passwordConfirmation: string;
  nickname: string;
  countryCode?: string | null;
  locale?: 'zh' | 'en';
  acceptTerms: boolean;
}

export interface RegisterPending {
  pendingVerification: true;
  email: string;
}

interface WireAuthResult {
  player?: WirePlayerSelf;
  csrf_token?: string;
  pending_verification?: boolean;
}

export const authQueries = {
  me: () =>
    queryOptions({
      queryKey: ['auth', 'me'] as const,
      queryFn: async (): Promise<Viewer | null> => {
        await ensureCsrf();
        try {
          return normalizeViewer(await api.get<WirePlayerSelf>(endpoints.auth.me()));
        } catch (error) {
          if (error instanceof ApiError && (error.isUnauthorized || error.isBanned)) return null;
          throw error;
        }
      },
      staleTime: 30_000,
      retry: false,
    }),

  sessions: () =>
    queryOptions({
      queryKey: ['account', 'sessions'] as const,
      queryFn: async (): Promise<AuthSession[]> => {
        const payload = await api.get<{ items?: WireSession[] }>(endpoints.account.sessions());
        return (payload.items ?? []).map(authSession);
      },
    }),

  gameTokens: () =>
    queryOptions({
      queryKey: ['account', 'game-tokens'] as const,
      queryFn: async (): Promise<GameToken[]> => {
        const payload = await api.get<{ items?: WireGameToken[] }>(endpoints.account.gameTokens());
        return (payload.items ?? []).map(gameToken);
      },
    }),

  device: () =>
    queryOptions({
      queryKey: ['account', 'device'] as const,
      queryFn: async (): Promise<DeviceBinding> =>
        deviceBinding(await api.get<WireDevice>(endpoints.account.device())),
    }),
};

export const authApi = {
  async login(input: LoginInput): Promise<Viewer> {
    const result = await api.post<WireAuthResult>(endpoints.auth.login(), {
      identifier: input.identifier,
      password: input.password,
      remember: Boolean(input.remember),
    });
    acceptCsrfToken(result.csrf_token);
    return normalizeViewer(result.player);
  },

  async register(input: RegisterInput): Promise<RegisterPending> {
    await api.post<WireAuthResult>(endpoints.auth.register(), {
      username: input.username,
      email: input.email,
      password: input.password,
      password_confirmation: input.passwordConfirmation,
      nickname: input.nickname,
      country_code: input.countryCode ?? null,
      locale: input.locale ?? 'zh',
      accept_terms: input.acceptTerms,
    });
    return { pendingVerification: true, email: input.email };
  },

  sendEmailCode(input: { email: string; purpose: 'register' | 'password_reset' }) {
    return api.post<{ sent: boolean; already_sent: boolean }>(endpoints.auth.emailSend(), {
      email: input.email,
      purpose: input.purpose,
    });
  },

  verifyEmail(input: { email: string; code: string; purpose?: 'register' }) {
    return api.post(endpoints.auth.emailVerify(), {
      email: input.email,
      code: input.code,
      purpose: input.purpose ?? 'register',
    });
  },

  forgotPassword(input: { email: string }) {
    return api.post<{ sent: boolean; already_sent: boolean }>(endpoints.auth.passwordForgot(), {
      email: input.email,
    });
  },

  resetPassword(input: { email: string; code: string; password: string; passwordConfirmation: string }) {
    return api.post(endpoints.auth.passwordReset(), {
      email: input.email,
      code: input.code,
      password: input.password,
      password_confirmation: input.passwordConfirmation,
    });
  },

  async logout(): Promise<void> {
    try {
      await api.post(endpoints.auth.logout());
    } finally {
      csrfStore.clear();
      await ensureCsrf();
    }
  },

  changePassword(input: { currentPassword: string; password: string; passwordConfirmation: string }) {
    return api.post(endpoints.account.password(), {
      current_password: input.currentPassword,
      password: input.password,
      password_confirmation: input.passwordConfirmation,
    });
  },

  changeEmail(input: { currentPassword: string; email: string; code: string }) {
    return api.post(endpoints.account.email(), {
      current_password: input.currentPassword,
      email: input.email,
      code: input.code,
    });
  },

  sendChangeEmailCode(input: { currentPassword: string; email: string }) {
    return api.post(endpoints.account.emailSend(), {
      purpose: 'change_email',
      current_password: input.currentPassword,
      email: input.email,
    });
  },

  sendAccountEmailCode(input: {
    purpose: 'bind_email' | 'verify_email' | 'change_email';
    email?: string;
    currentPassword?: string;
  }) {
    return api.post(endpoints.account.emailSend(), {
      purpose: input.purpose,
      email: input.email,
      current_password: input.currentPassword,
    });
  },

  verifyAccountEmail(input: {
    purpose: 'bind_email' | 'verify_email' | 'change_email';
    code: string;
    email?: string;
    currentPassword?: string;
  }) {
    return api.post(endpoints.account.emailVerify(), {
      purpose: input.purpose,
      code: input.code,
      email: input.email,
      current_password: input.currentPassword,
    });
  },

  revokeSession(id: string) {
    return api.delete(endpoints.account.session(id));
  },

  revokeOtherSessions() {
    return api.delete(endpoints.account.sessions());
  },

  revokeGameToken(id: number) {
    return api.delete(endpoints.account.gameToken(id));
  },

  deleteAccount(input: { currentPassword: string }) {
    return api.delete(endpoints.account.self(), { current_password: input.currentPassword, confirm: 'DELETE' });
  },
};
